import { Button, Dialog, Field, Input } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ApiError, type UserCreate, UsersService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface AddUserProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddAction = ({ isOpen, onClose }: AddUserProps) => {
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showToast = useCustomToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserCreate>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: UserCreate) => UsersService.createUser({ companySlug: slug, requestBody: data }),
    onSuccess: () => {
      showToast('Success!', 'Action created successfully.', 'success');
      reset();
      onClose();
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const onSubmit: SubmitHandler<UserCreate> = (data) => {
    mutation.mutate(data);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()} placement="center">
      <Dialog.Backdrop />
      <Dialog.Content as="form" onSubmit={handleSubmit(onSubmit)}>
        <Dialog.CloseTrigger />
        <Dialog.Header>
          <Dialog.Title>{t('user.drawer.add.title')}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Field.Root required invalid={!!errors.email}>
            <Field.Label>
              {t('user.columnsHeader.email')}
              <Field.RequiredIndicator />
            </Field.Label>
            <Input
              id="email"
              {...register('email', {
                required: t('user.errors.emailRequired'),
              })}
              placeholder={t('user.columnsHeader.email.placeholder')}
              type="text"
            />
            <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root mt={4} required invalid={!!errors.first_name}>
            <Field.Label>
              {t('user.columnsHeader.firstName')}
              <Field.RequiredIndicator />
            </Field.Label>
            <Input
              id="first_name"
              {...register('first_name', {
                required: t('user.errors.firstNameRequired'),
              })}
              placeholder={t('user.columnsHeader.firstName.placeholder')}
              type="text"
            />
            <Field.ErrorText>{errors.first_name?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root mt={4} required invalid={!!errors.last_name}>
            <Field.Label>
              {t('user.columnsHeader.lastName')}
              <Field.RequiredIndicator />
            </Field.Label>
            <Input
              id="last_name"
              {...register('last_name', {
                required: t('user.errors.lastNameRequired'),
              })}
              placeholder={t('user.columnsHeader.lastName.placeholder')}
              type="text"
            />
            <Field.ErrorText>{errors.last_name?.message}</Field.ErrorText>
          </Field.Root>
        </Dialog.Body>
        <Dialog.Footer>
          <Button onClick={onClose} variant="outline">
            {t('ui.cancel')}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {t('user.add.btn')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddAction;
